import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const ToastContext = createContext(null);

const VARIANTS = {
  success: { icon: 'fa-circle-check',         tone: 'border-success/30  bg-success-soft text-success' },
  error:   { icon: 'fa-circle-exclamation',   tone: 'border-rose-200    bg-rose-soft    text-rose-700' },
  info:    { icon: 'fa-circle-info',          tone: 'border-accent/20   bg-accent-soft  text-accent' },
  warning: { icon: 'fa-triangle-exclamation', tone: 'border-warning/30  bg-warning-soft text-warning' },
};

let counter = 0;

export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
    const t = timers.current.get(id);
    if (t) {
      clearTimeout(t);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (item) => {
      const id = ++counter;
      const variant = VARIANTS[item.variant] ? item.variant : 'info';
      const ttl = item.ttl ?? (variant === 'error' ? 6000 : 3500);
      setItems((prev) => [...prev, { id, variant, title: item.title, message: item.message }]);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), ttl),
      );
      return id;
    },
    [dismiss],
  );

  // Bulk-clear every queued toast. Used by the session-expiry handler
  // to wipe any action-error toasts that fired moments before the 401
  // landed — otherwise the operator sees both the action's error AND
  // the "session expired" warning side-by-side while the SPA swaps to
  // LoginPage.
  const dismissAll = useCallback(() => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current.clear();
    setItems([]);
  }, []);

  const api = useMemo(
    () => ({
      show: (opts) => push(opts),
      success: (message, title) => push({ variant: 'success', message, title }),
      error: (message, title) => push({ variant: 'error', message, title }),
      info: (message, title) => push({ variant: 'info', message, title }),
      warning: (message, title) => push({ variant: 'warning', message, title }),
      dismiss,
      dismissAll,
    }),
    [push, dismiss, dismissAll],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed top-5 right-5 z-[100] flex w-[min(420px,calc(100vw-2.5rem))] flex-col gap-2.5"
      >
        {items.map((t) => {
          const v = VARIANTS[t.variant];
          return (
            <div
              key={t.id}
              role="status"
              className={`pointer-events-auto rounded-2xl border ${v.tone} bg-white shadow-card`}
            >
              <div className="flex gap-3 px-4 py-3">
                <i className={`fa-solid ${v.icon} mt-0.5 text-base`} aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  {t.title && <div className="text-sm font-semibold text-ink">{t.title}</div>}
                  {t.message && (
                    <div className={`text-sm ${t.title ? 'mt-0.5 text-mist' : 'text-ink'}`}>
                      {t.message}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => dismiss(t.id)}
                  className="self-start text-mist transition-colors hover:text-ink"
                  aria-label="Dismiss"
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
